import { CompanyMember } from 'core/companyMember/CompanyMember';
import {
  ChannelGroupDTO,
  ChannelGroupFormData,
  ChannelGroupListDTO,
  ChannelGroupStatusFilter
} from './ChannelGroup';
import {
  ChannelGroupWebService,
  RestfulChannelGroupWebService
} from 'ws/ChannelGroupWebService';
import { CreativeSize } from 'core/creative/Creative';
import { ChannelGroupSetting } from './ChannelGroupSettings';

export interface ChannelGroupManager {
  /**
   * ECOPD-5701：`status` 省略 = 要**全部**渠道。只有渠道管理列表該帶它；
   * registry 與經銷商 bid price 兩條路徑依賴「不帶就是全部」，見 WebService 的註解。
   */
  getChannelGroups(
    useI18nName?: boolean,
    status?: ChannelGroupStatusFilter
  ): Promise<ChannelGroupListDTO[]>;
  getChannelGroup(id: number): Promise<ChannelGroupDTO>;
  createChannelGroup(formData: ChannelGroupFormData): Promise<void>;
  editChannelGroup(id: number, formData: ChannelGroupFormData): Promise<void>;
  addChannelGroupMember(
    id: number,
    email: string,
    roleName: string
  ): Promise<void>;
  deleteChannelGroupMember(
    id: number,
    email: string,
    roleName: string
  ): Promise<void>;
  getChannelGroupMembers(id: number): Promise<CompanyMember[]>;
  getOutdoorCreativeSizes(id: number): Promise<CreativeSize[]>;
  getCampaignAdTypesUsageStatus(
    adTypeIds: number[]
  ): Promise<Record<number, boolean>>;
  getAllowedSalesChannels(): Promise<SelectOptions[]>;
  /** ECOPD-5566：報表用的渠道清單，含停用渠道。 */
  getReportChannels(): Promise<SelectOptions[]>;
  /**
   * ECOPD-5572：PMax 自主停用渠道。⚠️ 破壞性，見實作註解。
   * ECOPD-5701：`reason` 必填，呼叫端要先 trim 並擋掉空值（後端空值回 400）。
   */
  archiveChannelGroup(id: number, reason: string): Promise<ChannelGroupDTO>;
  /** ECOPD-5572：解除 PMax 自停，**不恢復**任何被停掉的活動與訂單。 */
  unarchiveChannelGroup(id: number): Promise<ChannelGroupDTO>;
  getSimulcastGroups(id: number, isTesting?: boolean): Promise<SelectOptions[]>;
  getSpaceIdsOfSimulcastGroup(
    groupId: string,
    isTesting?: boolean
  ): Promise<string[]>;
}

export class DefaultChannelGroupManager implements ChannelGroupManager {
  constructor(
    private webService: ChannelGroupWebService = new RestfulChannelGroupWebService()
  ) {}

  async getChannelGroups(
    useI18nName?: boolean,
    status?: ChannelGroupStatusFilter
  ) {
    return this.webService.getChannelGroups(useI18nName, status);
  }

  async getChannelGroup(id: number) {
    return this.webService.getChannelGroup(id);
  }

  async createChannelGroup(formData: ChannelGroupFormData) {
    const channelGroup = await this.webService.createChannelGroup(formData);
    ChannelGroupSetting.register(channelGroup.name, channelGroup);
  }

  async editChannelGroup(id: number, formData: ChannelGroupFormData) {
    await this.webService.editChannelGroup(id, formData);
    // ECOPD-5572：一定要 await。少了它，這支回來時 registry 還停在編輯前的舊值，
    // 呼叫端接著做的渠道判定就讀到過期資料 —— 使用者按完存檔會覺得沒生效。
    try {
      await ChannelGroupSetting.refresh(this);
    } catch (e) {
      // 重載失敗**不能**讓已經成功的 PUT 被回報成失敗。這支的錯誤會一路傳到
      // `useCallAPI` 的 catch，變成「存檔失敗」的 toast 且不導頁 —— 但渠道其實
      // 已經改好了，使用者只會困惑地再存一次。
      //
      // `refresh()` 取資料失敗時不會動到 registry，所以這裡吞掉錯誤不會留下
      // 殘缺狀態；代價只是 registry 停在編輯前的舊值，直到下一次成功的重載。
      console.error('Failed to reload channel groups after editing', e);
    }
  }

  async addChannelGroupMember(
    id: number,
    email: string,
    roleName: string
  ): Promise<void> {
    await this.webService.addChannelGroupMember(id, email, roleName);
  }

  async deleteChannelGroupMember(
    id: number,
    email: string,
    roleName: string
  ): Promise<void> {
    await this.webService.deleteChannelGroupMember(id, email, roleName);
  }

  async getChannelGroupMembers(id: number) {
    return this.webService.getChannelGroupMembers(id);
  }

  async getOutdoorCreativeSizes(id: number) {
    return this.webService.getOutdoorCreativeSizes(id);
  }

  async getCampaignAdTypesUsageStatus(
    adTypeIds: number[]
  ): Promise<Record<number, boolean>> {
    return this.webService.getCampaignAdTypesUsageStatus(adTypeIds);
  }

  async getAllowedSalesChannels(): Promise<SelectOptions[]> {
    return this.webService.getAllowedSalesChannels();
  }

  async getReportChannels(): Promise<SelectOptions[]> {
    return this.webService.getReportChannels();
  }

  /**
   * ECOPD-5572：停用渠道，並**就地**把新狀態寫回 `ChannelGroupSetting`。
   *
   * 就地 register 而不是 `ChannelGroupSetting.refresh()`：refresh 會刪掉所有
   * 非 static 渠道再整包重新載入，成本大得多，而我們手上已經有這一筆的完整 DTO。
   *
   * ⚠️ 不更新 registry 的話，同一個 session 裡的群組選單、經銷商 add-on 頁、
   * 活動／素材的唯讀判定全都會停在舊值 —— 使用者會看到「按了沒反應」。
   * （這條路徑能成立的前提是 `register()` 的既存分支會更新旗標，見 `setArchiveFlags`。）
   */
  async archiveChannelGroup(
    id: number,
    reason: string
  ): Promise<ChannelGroupDTO> {
    const channelGroup = await this.webService.archiveChannelGroup(id, reason);
    ChannelGroupSetting.register(channelGroup.channel, channelGroup);
    return channelGroup;
  }

  /** ECOPD-5572：解除自停。同樣就地更新 registry。 */
  async unarchiveChannelGroup(id: number): Promise<ChannelGroupDTO> {
    const channelGroup = await this.webService.unarchiveChannelGroup(id);
    ChannelGroupSetting.register(channelGroup.channel, channelGroup);
    return channelGroup;
  }

  async getSimulcastGroups(
    id: number,
    isTesting?: boolean
  ): Promise<SelectOptions[]> {
    return this.webService.getSimulcastGroups(id, isTesting);
  }

  async getSpaceIdsOfSimulcastGroup(
    groupId: string,
    isTesting?: boolean
  ): Promise<string[]> {
    return this.webService.getSpaceIdsOfSimulcastGroup(groupId, isTesting);
  }
}
